import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  distinctUntilChanged,
  map,
  Observable,
  of,
  take,
} from 'rxjs';

import { MicrosoftApiService } from '@blaze-shared/connectors-data-access';
import { ConnectorConfiguration, MicrosoftServiceType } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
  SelectComponent,
  SharedModule,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ButtonComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';
import { mapObjectsToOptions, SelectOption } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';

import { ConnectorForm, OneDriveConfiguration } from './one-drive-connector-dialog.models';

@UntilDestroy()
@Component({
  selector: 'blaze-one-drive-connector-dialog',
  templateUrl: './one-drive-connector-dialog.component.html',
  styleUrls: ['./one-drive-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectComponent,
    DialogModule,
    AsyncPipe,
    EnvironmentIndicatorComponent,
    CopyConnectorConfigEnvButtonComponent,
    ButtonBarComponent,
    NgTemplateOutlet,
    ButtonComponent,
    IconsModule,
    ReactiveFormsModule,
    PanelComponent,
    SharedModule,
  ],
  host: { class: 'data-connector-dialog' },
})
export class OneDriveConnectorDialogComponent
  extends BaseConnectorDialogComponent<OneDriveConfiguration>
  implements OnInit
{
  private readonly microsoftApiService = inject(MicrosoftApiService);
  private readonly panelRef = inject(PanelRef);

  private readonly availableDriveId$$ = new BehaviorSubject<SelectOption<string>[]>([]);

  serviceName = 'microsoft';

  availableDriveId$ = this.availableDriveId$$.asObservable();
  connectorForm: FormGroup<ConnectorForm>;
  readonly isLoading = signal(false);

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: Validators.required }),
      driveId: new FormControl('', { nonNullable: true, validators: Validators.required }),
    });

    this.setupPhase$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        concatMap(() => this.loadDrives(this.authState)),
      )
      .subscribe((options) => {
        this.availableDriveId$$.next(options);
      });

    this.availableDriveId$
      .pipe(
        untilDestroyed(this),
        concatLatestFrom(() => this.connectorForm.controls.driveId.valueChanges),
      )
      .subscribe(([options, selectedDriveId]) => {
        if (options.some((option: any) => option.value === selectedDriveId)) {
          return;
        }
        this.connectorForm.controls.driveId.setValue('', { emitEvent: false });
      });

    this.withOAuth();
  }

  ngOnInit(): void {
    const configuration = this.getConfiguration(this.connector);
    this.fillForm(configuration);
    this.goToAuth({ reauthorize: true });
  }

  getConfiguration(connector?: ConnectorConfiguration | undefined): OneDriveConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const authState = this.authState ?? String(connector?.configuration?.authState ?? '');
    const driveId = String(connector?.configuration?.driveId ?? '');

    return { connectorId, connectorName, authState, driveId };
  }

  fillForm(configuration: OneDriveConfiguration): void {
    const { connectorId, connectorName, authState, driveId } = configuration;

    this.connectorId = connectorId;
    this.authState = authState;

    this.loadingDialog$$.next(true);
    this.loadDrives(authState)
      .pipe(untilDestroyed(this), take(1))
      .subscribe((options) => {
        this.connectorForm.setValue(
          {
            connectorName,
            driveId,
          },
          { emitEvent: false },
        );

        this.availableDriveId$$.next(options);
        this.loadingDialog$$.next(false);
      });
  }

  loadDrives(authState: string): Observable<SelectOption<string>[]> {
    if (!authState || !this.appInstalled()) {
      return of([]);
    }

    return this.microsoftApiService.getDrives(authState).pipe(
      catchError((error) => {
        this.handleErrors(error);
        return of([]);
      }),
      map((drives) => mapObjectsToOptions(drives) ?? []),
    );
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, driveId } = this.connectorForm.value;

    if (!connectorName || !driveId) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        authState: this.authState,
        driveId,
      },
    );

    this.isLoading.set(true);
    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
        this.isLoading.set(false);
      });
  }

  protected override getAuthorizationUrl(): Observable<{ url: string }> {
    return this.connectorsApiService.getServiceAuthorizeUrl(this.serviceName, {
      serviceType: MicrosoftServiceType.OneDrive,
    });
  }
}
